import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, ChartLine, Lock, Lightning } from "@phosphor-icons/react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";

export default function ForAdvertisers() {
    return (
        <div className="bg-paper text-ink min-h-screen">
            <Navbar />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-ink-200">
                <div className="absolute inset-0 grid-bg pointer-events-none" />
                <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-24 relative" data-testid="advertisers-hero">
                    <div className="max-w-3xl">
                        <p className="eyebrow mb-6">FOR BRANDS · USA · CANADA</p>
                        <h1 className="h1">
                            Real opinions,<br />
                            <span className="text-advertiser">from real people.</span>
                        </h1>
                        <p className="text-lg text-ink-600 mt-8 max-w-xl leading-relaxed">
                            SocietyIntent gives brands fast access to a vetted, paid panel of U.S. and Canadian residents — with quotas, branching logic, and anti-fraud that actually work. Launch a study in fifteen minutes.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Link to="/register?role=advertiser" className="btn btn-advertiser" data-testid="advertisers-cta-primary">
                                Become an advertiser <ArrowRight size={18} />
                            </Link>
                            <Link to="/contact" className="btn btn-outline" data-testid="advertisers-cta-talk">
                                Talk to sales
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* What you get */}
            <section className="border-b border-ink-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
                    <p className="eyebrow mb-3">WHAT YOU GET</p>
                    <h2 className="text-3xl sm:text-4xl font-semibold mb-12 max-w-2xl">A market-research stack that doesn't waste your budget on bots.</h2>
                    <div className="grid md:grid-cols-3 gap-0 border border-ink">
                        <ValueProp
                            icon={<Lightning size={28} weight="duotone" className="text-advertiser" />}
                            title="Live in fifteen minutes"
                            body="Drag-and-drop builder, quotas, branching, attention checks. No code. Reviewed by humans, usually within two business hours."
                            testid="advertisers-value-speed"
                        />
                        <ValueProp
                            icon={<Lock size={28} weight="duotone" className="text-advertiser" />}
                            title="Quality you can trust"
                            body="Multi-layered anti-fraud: speed checks, attention checks, quota enforcement, IP/device uniqueness, VPN detection. Bad responses don't reach your dashboard."
                            testid="advertisers-value-quality"
                        />
                        <ValueProp
                            icon={<ChartLine size={28} weight="duotone" className="text-advertiser" />}
                            title="Stream-in analytics"
                            body="Live activity feed pushes new completes over WebSocket. CSV / JSON export from the first response. No waiting for nightly batches."
                            testid="advertisers-value-analytics"
                        />
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="border-b border-ink-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
                    <p className="eyebrow mb-3">PRICING</p>
                    <h2 className="text-3xl sm:text-4xl font-semibold mb-3 max-w-2xl">No subscriptions. No minimums. No surprises.</h2>
                    <p className="text-ink-600 max-w-2xl mb-10">Pay per completed response. The price is shown up front before you submit, and you only pay when respondents actually finish your survey.</p>

                    <div className="grid md:grid-cols-3 gap-0 border border-ink">
                        <PricingCol
                            label="Respondent payout"
                            value="$1.00"
                            unit="per complete"
                            footer="100 points = $1 USD. Members are paid via PayPal, Stripe direct deposit, or gift card."
                            testid="advertisers-pricing-respondent"
                        />
                        <PricingCol
                            label="Platform fee"
                            value="25%"
                            unit="of respondent payout"
                            footer="Covers panel acquisition, fraud-prevention, infrastructure, and human review of every survey."
                            testid="advertisers-pricing-platform"
                        />
                        <PricingCol
                            label="Total advertiser cost"
                            value="$1.25"
                            unit="per complete"
                            footer="So a 200-response study costs $250 total — flat. Quotas and tight targeting may surge per-complete pricing transparently."
                            testid="advertisers-pricing-total"
                            highlight
                        />
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="border-b border-ink-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
                    <p className="eyebrow mb-3">HOW IT WORKS</p>
                    <h2 className="text-3xl sm:text-4xl font-semibold mb-12 max-w-2xl">From blank dashboard to live study, in five steps.</h2>
                    <ol className="space-y-6 max-w-3xl">
                        <Step n="1" title="Apply for an advertiser account" body="Tell us about your business. We approve most accounts within one business day." testid="advertisers-step-1" />
                        <Step n="2" title="Build your survey" body="Drag-and-drop questions, set targeting, configure quotas and branching. Save drafts as you go." testid="advertisers-step-2" />
                        <Step n="3" title="Pre-fund via Stripe" body="The price is computed up front. Pay securely with any major card. We don't release funds until your study is approved." testid="advertisers-step-3" />
                        <Step n="4" title="Human review" body="Our team checks PII handling, branching sanity, attention checks. Most reviews finish within two business hours." testid="advertisers-step-4" />
                        <Step n="5" title="Watch results stream in" body="Live activity feed, on-page analytics, CSV/JSON export. Top up mid-flight if you want more responses." testid="advertisers-step-5" />
                    </ol>
                </div>
            </section>

            {/* Trust signals */}
            <section className="border-b border-ink-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
                    <p className="eyebrow mb-3">TRUST</p>
                    <h2 className="text-3xl sm:text-4xl font-semibold mb-12 max-w-2xl">Built for grown-up procurement.</h2>
                    <div className="grid sm:grid-cols-2 gap-6">
                        <TrustItem text="SOC2-style controls in place; pen-tested twice a year." />
                        <TrustItem text="Standard MSA / DPA on request. We don't resell your data." />
                        <TrustItem text="Compliant with TCPA, CAN-SPAM, CASL, CCPA/CPRA, PIPEDA." />
                        <TrustItem text="Pseudonymized response data — advertisers never see PII." />
                        <TrustItem text="Aggregations only render at cell size ≥ 10 to prevent re-identification." />
                        <TrustItem text="Refunds for unfilled responses, processed within five business days." />
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section>
                <div className="max-w-3xl mx-auto px-6 lg:px-8 py-20 text-center">
                    <h2 className="text-3xl sm:text-4xl font-semibold mb-6">Ready to launch a study?</h2>
                    <p className="text-ink-600 mb-10 leading-relaxed">
                        Sign up free — you only pay when you submit a study. Or talk to our team if you want a walk-through first.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <Link to="/register?role=advertiser" className="btn btn-advertiser" data-testid="advertisers-cta-bottom">
                            Become an advertiser <ArrowRight size={18} />
                        </Link>
                        <Link to="/help?category=getting-started-advertisers" className="btn btn-outline" data-testid="advertisers-help-link">
                            Read the docs
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function ValueProp({ icon, title, body, testid }) {
    return (
        <div className="p-8 border-r border-ink last:border-r-0 border-b border-ink md:border-b-0" data-testid={testid}>
            <div className="mb-4">{icon}</div>
            <h3 className="text-lg font-semibold mb-2">{title}</h3>
            <p className="text-ink-600 leading-relaxed text-sm">{body}</p>
        </div>
    );
}

function PricingCol({ label, value, unit, footer, testid, highlight = false }) {
    return (
        <div
            className={`p-8 border-r border-ink last:border-r-0 border-b border-ink md:border-b-0 ${highlight ? "bg-ink text-paper" : ""}`}
            data-testid={testid}
        >
            <p className={`eyebrow mb-3 ${highlight ? "text-paper" : ""}`}>{label}</p>
            <div className="flex items-baseline gap-2 mb-3">
                <span className="text-4xl font-semibold font-mono">{value}</span>
                <span className={`text-sm ${highlight ? "text-paper" : "text-ink-600"}`}>{unit}</span>
            </div>
            <p className={`text-sm leading-relaxed ${highlight ? "text-paper opacity-80" : "text-ink-600"}`}>{footer}</p>
        </div>
    );
}

function Step({ n, title, body, testid }) {
    return (
        <li className="flex gap-5" data-testid={testid}>
            <div className="font-mono text-2xl text-ink-300 shrink-0 w-12">{n}</div>
            <div>
                <h3 className="text-base font-semibold mb-1">{title}</h3>
                <p className="text-ink-600 leading-relaxed">{body}</p>
            </div>
        </li>
    );
}

function TrustItem({ text }) {
    return (
        <div className="flex items-start gap-3">
            <CheckCircle size={20} weight="duotone" className="text-advertiser mt-0.5 shrink-0" />
            <p className="text-ink-700 leading-relaxed">{text}</p>
        </div>
    );
}
